import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Checkbox,
  CheckboxGroup,
  Stack,
  Alert,
  AlertIcon,
  Spinner,
  Divider,
  Badge,
  SimpleGrid,
  useToast,
  FormControl,
  FormLabel,
  Switch,
  Select,
} from '@chakra-ui/react';
import CrosstabDisplay from './CrosstabDisplay';
import fetchWithAuth from '../../utils/fetchWithAuth';

/**
 * CrosstabBuilder - Interactive crosstab generation interface
 *
 * Allows users to:
 * - Select demographic dimensions
 * - Choose questions to analyze
 * - Apply weighting
 * - View results with statistics
 */
function CrosstabBuilder({ poll, pollId }) {
  const [dimensions, setDimensions] = useState([]);
  const [selectedDimensions, setSelectedDimensions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [useWeights, setUseWeights] = useState(false);
  const [confidenceLevel, setConfidenceLevel] = useState(0.95);
  const [loading, setLoading] = useState(false);
  const [crosstabs, setCrosstabs] = useState(null);
  const [error, setError] = useState(null);
  const [hasWeights, setHasWeights] = useState(false);
  const toast = useToast();

  useEffect(() => {
    fetchAvailableDimensions();
    checkForWeights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId]);

  const fetchAvailableDimensions = async () => {
    try {
      const response = await fetchWithAuth(`/api/polls/${pollId}/available-demographics/`);
      if (!response.ok) {
        throw new Error('Failed to fetch demographics');
      }
      const data = await response.json();
      setDimensions(data.dimensions || []);
    } catch (err) {
      console.error('Error fetching dimensions:', err);
      toast({
        title: 'Error',
        description: 'Failed to load demographic dimensions',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const checkForWeights = async () => {
    try {
      const response = await fetchWithAuth(`/api/polls/${pollId}/active-weights/`);
      if (response.ok) {
        const data = await response.json();
        setHasWeights(data.has_weights || false);
      }
    } catch (err) {
      console.error('Error checking weights:', err);
    }
  };

  const handleGenerateCrosstabs = async () => {
    if (selectedDimensions.length === 0) {
      toast({
        title: 'No dimensions selected',
        description: 'Please select at least one demographic dimension',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (selectedQuestions.length === 0) {
      toast({
        title: 'No questions selected',
        description: 'Please select at least one question',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetchWithAuth(`/api/polls/${pollId}/crosstab/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question_ids: selectedQuestions,
          dimensions: selectedDimensions,
          weighted: useWeights,
          confidence_level: confidenceLevel,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate crosstabs');
      }

      const data = await response.json();
      setCrosstabs(data.crosstabs || []);

      toast({
        title: 'Success!',
        description: `Generated ${data.count} crosstab${data.count !== 1 ? 's' : ''}`,
        status: 'success',
        duration: 3000,
      });
    } catch (err) {
      console.error('Error generating crosstabs:', err);
      setError(err.message);
      toast({
        title: 'Error',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAllDimensions = () => {
    if (selectedDimensions.length === dimensions.length) {
      setSelectedDimensions([]);
    } else {
      setSelectedDimensions([...dimensions]);
    }
  };

  const handleSelectAllQuestions = () => {
    const questionIds = poll.questions?.map(q => q.id) || [];
    if (selectedQuestions.length === questionIds.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(questionIds);
    }
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Instructions */}
      <Alert status="info" variant="left-accent">
        <AlertIcon />
        <Box>
          <Text fontWeight="medium">Generate Demographic Crosstabs</Text>
          <Text fontSize="sm" mt={1}>
            Crosstabs show how different demographic groups responded to each question.
            Select the dimensions and questions you want to analyze, then click Generate.
          </Text>
        </Box>
      </Alert>

      {/* Configuration Section */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Demographic Dimensions */}
        <Box
          p={5}
          bg="white"
          borderRadius="md"
          borderWidth="1px"
          borderColor="gray.200"
        >
          <HStack justify="space-between" mb={4}>
            <Heading size="sm">Demographic Dimensions</Heading>
            <Button size="xs" variant="link" onClick={handleSelectAllDimensions}>
              {selectedDimensions.length === dimensions.length ? 'Deselect All' : 'Select All'}
            </Button>
          </HStack>

          {dimensions.length === 0 ? (
            <Text color="gray.500" fontSize="sm">
              No demographic data available
            </Text>
          ) : (
            <CheckboxGroup
              value={selectedDimensions}
              onChange={setSelectedDimensions}
            >
              <Stack spacing={2} maxH="300px" overflowY="auto">
                {dimensions.map(dim => (
                  <Checkbox key={dim} value={dim}>
                    <Text fontSize="sm">{dim}</Text>
                  </Checkbox>
                ))}
              </Stack>
            </CheckboxGroup>
          )}

          <Text fontSize="xs" color="gray.500" mt={3}>
            {selectedDimensions.length} dimension{selectedDimensions.length !== 1 ? 's' : ''} selected
          </Text>
        </Box>

        {/* Questions */}
        <Box
          p={5}
          bg="white"
          borderRadius="md"
          borderWidth="1px"
          borderColor="gray.200"
        >
          <HStack justify="space-between" mb={4}>
            <Heading size="sm">Questions to Analyze</Heading>
            <Button size="xs" variant="link" onClick={handleSelectAllQuestions}>
              {selectedQuestions.length === (poll.questions?.length || 0) ? 'Deselect All' : 'Select All'}
            </Button>
          </HStack>

          {!poll.questions || poll.questions.length === 0 ? (
            <Text color="gray.500" fontSize="sm">
              No questions in this poll
            </Text>
          ) : (
            <CheckboxGroup
              value={selectedQuestions}
              onChange={setSelectedQuestions}
            >
              <Stack spacing={2} maxH="300px" overflowY="auto">
                {poll.questions.map(question => (
                  <Checkbox key={question.id} value={question.id}>
                    <Text fontSize="sm" noOfLines={2}>
                      Q{question.id}: {question.text}
                    </Text>
                  </Checkbox>
                ))}
              </Stack>
            </CheckboxGroup>
          )}

          <Text fontSize="xs" color="gray.500" mt={3}>
            {selectedQuestions.length} question{selectedQuestions.length !== 1 ? 's' : ''} selected
          </Text>
        </Box>
      </SimpleGrid>

      {/* Options */}
      <Box
        p={5}
        bg="gray.50"
        borderRadius="md"
        borderWidth="1px"
        borderColor="gray.200"
      >
        <Heading size="sm" mb={4}>Options</Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl display="flex" alignItems="center">
            <FormLabel htmlFor="use-weights" mb={0} fontSize="sm">
              Apply Demographic Weights
              {!hasWeights && (
                <Badge ml={2} colorScheme="orange" fontSize="xs">
                  No weights configured
                </Badge>
              )}
            </FormLabel>
            <Switch
              id="use-weights"
              isChecked={useWeights}
              onChange={(e) => setUseWeights(e.target.checked)}
              isDisabled={!hasWeights}
              colorScheme="blue"
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">Confidence Level</FormLabel>
            <Select
              value={confidenceLevel}
              onChange={(e) => setConfidenceLevel(parseFloat(e.target.value))}
              size="sm"
            >
              <option value={0.90}>90% (z = 1.645)</option>
              <option value={0.95}>95% (z = 1.960)</option>
              <option value={0.99}>99% (z = 2.576)</option>
            </Select>
          </FormControl>
        </SimpleGrid>

        {useWeights && !hasWeights && (
          <Alert status="warning" mt={3} fontSize="sm">
            <AlertIcon />
            No active weighting scheme. Visit the Weighting tab to configure weights.
          </Alert>
        )}
      </Box>

      {/* Generate Button */}
      <Button
        colorScheme="blue"
        size="lg"
        onClick={handleGenerateCrosstabs}
        isLoading={loading}
        loadingText="Generating Crosstabs..."
        isDisabled={selectedDimensions.length === 0 || selectedQuestions.length === 0}
      >
        Generate Crosstabs
      </Button>

      {/* Error Display */}
      {error && (
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Results */}
      {loading && (
        <VStack py={8} spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text>Calculating crosstabs with statistical tests...</Text>
        </VStack>
      )}

      {crosstabs && crosstabs.length > 0 && (
        <>
          <Divider />
          <Heading size="md">Results</Heading>
          <VStack spacing={6} align="stretch">
            {crosstabs.map((crosstab, index) => (
              <CrosstabDisplay
                key={index}
                crosstab={crosstab}
                index={index}
              />
            ))}
          </VStack>
        </>
      )}

      {crosstabs && crosstabs.length === 0 && (
        <Alert status="info">
          <AlertIcon />
          No crosstab data generated. This may happen if there are no responses yet.
        </Alert>
      )}
    </VStack>
  );
}

export default CrosstabBuilder;
